import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {DATE_RANGE_OPTIONS, MILLIS_PER_HOUR} from '../../lib/timeline';
import DateTimeInput from '../../components/DateTimeInput';
import Button from '../../components/Button';
import Input from '../../components/Input';

// No date restriction. Kept local to this screen, since DATE_RANGE_OPTIONS is shared with other screens.
const ALL_TIME = 0;
const CUSTOM_RANGE = -1;
const RANGE_OPTIONS = [{label: 'All time', range: ALL_TIME}, ...DATE_RANGE_OPTIONS];

// Sits on the page rather than in the Filter Data panel, so the date range can be changed in one step. It applies
// through the same filters as the panel, keeping that panel's question and review filters as they are.
// inline is for the screen's header, beside the Options button, where it needs no spacing of its own. disabled is set
// while rows are bulk deleted, since applying a range reloads the screen and would stop the delete.
function SheetDateRange({filters, onApplyFilters, inline, disabled}) {
  const appliedRange = getInitialRange(filters && filters.minDate, filters && filters.maxDate);
  const [range, setRange] = useState(appliedRange);
  const [minDate, setMinDate] = useState(filters ? filters.minDate : null);
  const [maxDate, setMaxDate] = useState(filters ? filters.maxDate : null);
  const [validation, setValidation] = useState(null);
  // The custom start and end dates are picked in a popup, so they don't widen the header.
  const [customOpen, setCustomOpen] = useState(false);
  // Set by any press inside the popup, including in the date pickers' calendars, which open in a layer of their own and
  // so aren't inside it on the page. React still passes their events up through the popup.
  const pressedInside = useRef(false);

  const applyRange = (minDate, maxDate) => {
    onApplyFilters({and: true, reviewState: '', filters: [], ...filters, minDate, maxDate});
  };

  // Closing without applying goes back to the range that is applied.
  const cancelCustomRange = () => {
    setCustomOpen(false);
    setValidation(null);
    setRange(appliedRange);
    setMinDate(filters ? filters.minDate : null);
    setMaxDate(filters ? filters.maxDate : null);
  };

  const cancelRef = useRef(cancelCustomRange);
  cancelRef.current = cancelCustomRange;

  useEffect(() => {
    if (!customOpen) {
      return undefined;
    }

    const handleMouseDown = () => {
      if (pressedInside.current) {
        pressedInside.current = false;
      }
      else {
        cancelRef.current();
      }
    };
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        cancelRef.current();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [customOpen]);

  const handleRangeChange = e => {
    const range = Number(e.target.value);
    setRange(range);
    setValidation(null);

    if (range === ALL_TIME) {
      setCustomOpen(false);
      applyRange(null, null);
    }
    else if (range > 0) {
      setCustomOpen(false);
      const now = new Date();
      applyRange(new Date(now.getTime() - range), now);
    }
    else {
      // A custom range waits for both dates and the Apply button, since picking a date and time changes them several
      // times and each change would reload the data.
      setCustomOpen(true);
    }
  };

  const handleApplyCustomRange = () => {
    if (!minDate || !maxDate) {
      setValidation('Please enter a start date and end date');
    }
    else if (minDate > maxDate) {
      setValidation('The start date must be before the end date');
    }
    else {
      setCustomOpen(false);
      applyRange(minDate, maxDate);
    }
  };

  return (
    <Container $inline={inline}>
      <Input
        id="filter_date_range"
        type="select"
        label="Date Range"
        labelPosition="start"
        value={range}
        disabled={disabled}
        onChange={handleRangeChange}
      >
        {RANGE_OPTIONS.map(option => (
          <option key={option.range} value={option.range}>{option.label}</option>
        ))}
      </Input>
      {/* The select doesn't change when Custom is picked again, so the applied dates open the popup to change them. */}
      {appliedRange === CUSTOM_RANGE && !customOpen && (
        <Button tertiary disabled={disabled} onClick={() => setCustomOpen(true)}>
          {getDateRangeLabel(filters)}
        </Button>
      )}
      {customOpen && (
        <CustomRangePopup
          $inline={inline}
          role="dialog"
          aria-label="Custom date range"
          onMouseDownCapture={() => { pressedInside.current = true; }}>
          <DateTimeInput
            placeholder="Start Date"
            date={minDate}
            onChange={setMinDate}
            invalid={!!validation}
          />
          <DateTimeInput
            placeholder="End Date"
            date={maxDate}
            onChange={setMaxDate}
            invalid={!!validation}
          />
          {validation && <Validation>{validation}</Validation>}
          <PopupButtons>
            <Button tertiary onClick={cancelCustomRange}>Cancel</Button>
            <Button disabled={disabled} onClick={handleApplyCustomRange}>Apply</Button>
          </PopupButtons>
        </CustomRangePopup>
      )}
    </Container>
  );
}

function getInitialRange(minDate, maxDate) {
  if (!minDate || !maxDate) {
    return ALL_TIME;
  }
  const diff = maxDate.getTime() - minDate.getTime();
  const match = DATE_RANGE_OPTIONS.find(option => option.range > 0 && Math.abs(option.range - diff) < MILLIS_PER_HOUR);
  return match ? match.range : CUSTOM_RANGE;
}

// What the applied range is called, for showing it outside the select: a preset's name, or a custom range's dates.
// Null when there is no date range.
export function getDateRangeLabel(filters) {
  const range = getInitialRange(filters && filters.minDate, filters && filters.maxDate);
  if (range === ALL_TIME) {
    return null;
  }
  if (range === CUSTOM_RANGE) {
    return `${formatDate(filters.minDate)} – ${formatDate(filters.maxDate)}`;
  }
  return RANGE_OPTIONS.find(option => option.range === range).label;
}

function formatDate(date) {
  return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: ${props => props.$inline ? '0' : '0 1.875rem'};
  margin-top: ${props => props.$inline ? '0' : '1rem'};
`;

// Opens under the select. In the header it lines up with the right edge so it stays on the screen.
const CustomRangePopup = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  ${props => props.$inline ? "right: 0;" : "left: 1.875rem;"}
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 18rem;
  padding: 1rem;
  background: #fff;
  border-radius: 0.625rem;
  box-shadow: 0 0.25rem 0.5rem rgba(90, 97, 105, 0.12), 0 0.4375rem 4.375rem rgba(90, 97, 105, 0.1);
`;

const PopupButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const Validation = styled.small`
  font-size: 0.875rem;
  color: #dc3545;
`;

export default SheetDateRange;
